import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { fade, openClose } from '@core/animations/animations';
import { createId } from '@core/util/file';
import { FontInfo } from '@core/util/font';
import { Font } from '@models/font';
import { BusinessAddress, DocumentFooters } from '@models/businesses';
import { DocumentLocation } from '@models/documents';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot, ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { BusinessAddressesService } from '@repos/businesses/business-addresses.service';
import { BusinessesServiceRepo } from '@repos/businesses/businesses.service';
import { BusinessDispatch, BusinessState } from '@state/business';
import {
	FileUpload,
	FileUploadState,
} from '@state/file-upload/fileUpload.state';
import { UploadFileStatus } from '@state/utils/models';
import { EventObj } from '@tinymce/tinymce-angular/editor/Events';
import { cloneDeep } from 'lodash';
import { Observable, of, Subject } from 'rxjs';
import {
	debounceTime,
	filter,
	map,
	switchMap,
	takeUntil,
} from 'rxjs/operators';
import { ButtonGroupOption } from '../../../../../shared/atomic/atoms/button-group/button-group.component';
import { UserProfileState } from '@state/user-profile';

enum FooterName {
	FIRST = 'first',
	GLOBAL = 'global',
}

@UntilDestroy()
@Component({
	selector: 'app-letterhead-editor',
	templateUrl: './letterhead-editor-dialog.component.html',
	styleUrls: ['./letterhead-editor-dialog.component.scss'],
	animations: [fade(100), openClose()],
	standalone: false,
})
export class LetterheadEditorDialogComponent implements OnDestroy {
	@ViewSelectSnapshot(UserProfileState.getCurrentBusinessId) businessId;
	busy: boolean;
	_content: string;

	get content(): string {
		return this._content;
	}

	set content(value: string) {
		this.updateContentSubject.next(value);
	}

	public updateContentSubject = new Subject<string>();

	public currentFooter: FooterName;
	@SelectSnapshot(BusinessState.getDefaultCloserFooter)
	defaultFooter: DocumentFooters;
	@SelectSnapshot(BusinessState.getDefaultLetterhead) defaultLetterhead: string;
	@SelectSnapshot(BusinessState.getFooter) businessFooter: DocumentFooters;
	@SelectSnapshot(BusinessState.getDefaultFont) defaultFont: Font;
	@SelectSnapshot(BusinessState.getDefaultFontSize) defaultFontSize: number;
	@SelectSnapshot(BusinessState.getDefaultLineHeight) defaultLineHeight: number;

	public fileName = 'logo';
	@Select(FileUploadState.getFileStatus) public fileUploadStatusFn$: Observable<
		(filter: string) => FileUpload
	>;
	footer: DocumentFooters;
	FooterName = FooterName;
	public footers: ButtonGroupOption<FooterName>[];
	globalFooter: boolean;
	letterhead: string;
	@SelectSnapshot(BusinessState.getLetterhead) businessLetterhead: string;
	@ViewSelectSnapshot(BusinessState.getLogoUrl) logoUrl: string;
	location = DocumentLocation.BUSINESS;
	otherContent: string;
	save: boolean;
	tmceInit: unknown;

	private fileUploadReset$: Subject<void>;

	constructor(
		private businessDispatch: BusinessDispatch,
		private businessAddressesService: BusinessAddressesService,
		private businessesServiceRepo: BusinessesServiceRepo,
		private changeDetectorRef: ChangeDetectorRef,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			mode: 'footer' | 'letterhead';
			officeId?: string;
			officeName?: string;
		},
		private dialogRef: MatDialogRef<LetterheadEditorDialogComponent>,
	) {
		this.updateContentSubject
			.pipe(debounceTime(500), untilDestroyed(this))
			.subscribe((content) => {
				this._content = content;
			});
		this.busy = true;
		this.save = false;
		this.currentFooter = FooterName.GLOBAL;
		this.fileUploadReset$ = new Subject<void>();
		this.footers = [
			{
				label: 'First page',
				value: FooterName.FIRST,
			},
			{
				label: 'All other pages',
				value: FooterName.GLOBAL,
			},
		];
		this.businessAddressesService
			.All()
			.pipe(
				untilDestroyed(this),
				map((addresses) => [
					...addresses.map((address) => address as BusinessAddress),
				]),
				map((addresses) =>
					addresses.find((address) => address.id === this.data.officeId),
				),
				map(
					(address) =>
						[
							address?.footer || this.businessFooter || this.defaultFooter,
							address?.letterhead ||
								this.businessLetterhead ||
								this.defaultLetterhead,
						] as [DocumentFooters, string],
				),
			)
			.subscribe(([footer, letterhead]) => {
				this.footer = cloneDeep(footer);
				this.letterhead = letterhead;
				switch (this.data.mode) {
					case 'footer':
						this.content = this.footer[this.currentFooter].content;
						break;
					case 'letterhead':
						this.content = this.letterhead;
						break;
				}
				this.setGlobalFooterSwitch();
			});
		this.tmceInit = {
			advlist_number_styles:
				'default,lower-alpha,lower-roman,upper-alpha,upper-roman',
			base_url: '/tinymce',
			branding: false,
			browser_spellcheck: true,
			content_style: `
			@import url('https://fonts.googleapis.com/css2?family=Nunito:ital,wght@0,400;0,700;1,400;1,700&display=swap');
			@font-face {
				font-family: 'calibri';
				src: url('/assets/fonts/Calibri.woff2') format('woff2'), url('/assets/fonts/Calibri.woff') format('woff');
			}
			* { font-family: ${FontInfo[this.defaultFont].editorFont}; }
			p, li, td, th { font-size: ${this.defaultFontSize}pt; line-height: ${this.defaultLineHeight}; }
			h1 { font-size: 21pt; }
			h2 { font-size: 18pt; }
			h3 { font-size: 16pt; }
			h4 { font-size: 14pt; }
			h5 { font-size: 12pt; }
			h6 { font-size: 12pt; }
			.mceNonEditable { background-color: rgb(241, 255, 248); color: rgb(21, 132, 73); font-size: calc(1em - 2px); padding: 1px 3px; }
			table { margin-top: -6px; margin-bottom: -6px; }
			img { display: inline-block; height: auto; max-width: 100% }
			thead { background-color: #f3f3f3; }
			th { text-align: left; }
			td { vertical-align: top; }
			.table_zebra_striped tbody tr:nth-child(even) { background-color: #f6f6f6; }
			`,
			contextmenu:
				'undo redo | copy paste pastetext | lists| outdent indent | bold italic forecolor | removeformat',
			elementpath: false,
			fix_list_elements: true,
			font_size_formats:
				'5pt 6pt 7pt 8pt 9pt 10pt 11pt 12pt 13pt 14pt 15pt 16pt 17pt 18pt 19pt 20pt 21pt 22pt 23pt 24pt 36pt 48pt',
			forced_root_block: 'p',
			formats: {
				alignleft: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table',
					styles: { textAlign: 'left' },
				},
				aligncenter: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table',
					styles: { textAlign: 'center' },
				},
				alignright: {
					selector: 'p,h1,h2,h3,h4,h5,h6,td,th,div,ul,ol,li,table',
					styles: { textAlign: 'right' },
				},
			},
			height: '100%',
			highlight_on_focus: false,
			image_advtab: false,
			image_uploadtab: true,
			images_file_types: 'jpeg,jpg,jpe,png,gif',
			images_reuse_filename: true,
			images_upload_handler: (blobInfo) => {
				return new Promise((resolve, reject) => {
					this.busy = true;
					this.save = true;
					this.fileUploadReset$.next();

					const DocId = createId();

					this.fileUploadStatusFn$
						.pipe(
							untilDestroyed(this),
							takeUntil(this.fileUploadReset$),
							switchMap((filterFn) => of(filterFn(DocId))),
							filter((status) => !!status),
							filter(
								(status) =>
									status.status === UploadFileStatus.SUCCESS ||
									status.status === UploadFileStatus.FAILED,
							),
						)
						.subscribe((status) => {
							switch (status.status) {
								case UploadFileStatus.FAILED:
									reject(status.error);
									break;
								case UploadFileStatus.SUCCESS:
									resolve(status.publicUrl);
									break;
							}

							this.busy = false;
							this.save = false;
						});

					this.businessDispatch.UploadBusinessFile(
						DocId,
						blobInfo.filename(),
						blobInfo.blob(),
					);
				});
			},
			indent_use_margin: true,
			line_height_formats: '1 1.1 1.15 1.2 1.3 1.4 1.5 1.6 2 2.5 3.0',
			menubar: false,
			noneditable_regexp: /\{?\{\{[^}]+\}\}\}?/gm, // matches {{handlebar}} and {{{handlebar}}}
			pagebreak_separator: '<span class="pdf-pagebreak-before">&nbsp;</span>',
			pagebreak_split_block: true,
			paste_block_drop: true,
			paste_data_images: true,
			plugins: 'lists advlist image table code pagebreak',
			quickbars_image_toolbar: 'alignleft aligncenter alignright',
			quickbars_insert_toolbar: 'hr | quicktable quickimage',
			quickbars_selection_toolbar:
				'fontsizeselect | forecolor bold italic underline strikethrough | removeformat | lineheight alignleft aligncenter alignright alignjustify | bullist numlist | copy paste pastetext',
			resize: false,
			statusbar: false,
			suffix: '.min',
			table_advtab: true,
			table_appearance_options: true,
			table_border_styles: [
				{ title: 'Solid', value: 'solid' },
				{ title: 'Inset', value: 'inset' },
				{ title: 'Double', value: 'double' },
				{ title: 'Outset', value: 'outset' },
				{ title: 'None', value: 'none' },
				{ title: 'Hidden', value: 'hidden' },
			],
			table_border_widths: [
				{ title: '1px', value: '1px' },
				{ title: '2px', value: '2px' },
				{ title: '3px', value: '3px' },
				{ title: '4px', value: '4px' },
				{ title: '5px', value: '5px' },
			],
			table_cell_advtab: true,
			table_class_list: [
				{
					title: 'None',
					value: 'table_no_style',
				},
				{
					title: 'Zebra striped',
					value: 'table_zebra_striped',
				},
			],
			table_default_attributes: {},
			table_default_styles: {
				border: 'none',
				width: '100%',
			},
			table_header_type: 'sectionCells',
			table_row_advtab: false,
			table_sizing_mode: 'relative',
			table_style_by_css: true,
			table_use_colgroups: false,
			text_patterns: [
				{ start: '*', end: '*', format: 'italic' },
				{ start: '**', end: '**', format: 'bold' },
				{ start: '#', format: 'h1', trigger: 'space' },
				{ start: '##', format: 'h2', trigger: 'space' },
				{ start: '###', format: 'h3', trigger: 'space' },
				{ start: '####', format: 'h4', trigger: 'space' },
				{ start: '#####', format: 'h5', trigger: 'space' },
				{ start: '######', format: 'h6', trigger: 'space' },
				{ start: '1.', cmd: 'InsertOrderedList', trigger: 'space' },
				{ start: '*', cmd: 'InsertUnorderedList', trigger: 'space' },
				{ start: '-', cmd: 'InsertUnorderedList', trigger: 'space' },
				{ start: '---', cmd: 'InsertHorizontalRule', trigger: 'space' },
				{ start: '--', replacement: '—' },
				{ start: '(c)', replacement: '©' },
			],
			toolbar: [
				{
					items: ['undo', 'redo'],
					name: 'history',
				},
				{
					items: ['fontsize'],
					name: 'font',
				},
				{
					items: ['blocks', 'forecolor'],
					name: 'fontLook',
				},
				{
					items: ['bold', 'italic', 'underline', 'strikethrough'],
					name: 'style',
				},
				{
					items: ['removeformat'],
					name: 'override',
				},
				{
					items: ['lineheight'],
					name: 'adjustment',
				},
				{
					items: ['alignleft', 'aligncenter', 'alignright', 'alignjustify'],
					name: 'alignment',
				},
				{
					items: ['bullist', 'numlist', 'table', 'image'],
					name: 'extra',
				},
				{
					items: ['hr'],
					name: 'page',
				},
				{
					items: ['outdent', 'indent'],
					name: 'format',
				},
				{
					items: ['code'],
					name: 'code',
				},
			],
			toolbar_mode: 'sliding',
		};
	}

	ngOnDestroy(): void {
		this.fileUploadReset$.complete();
	}

	cancelModal(): void {
		this.dialogRef.close();
	}

	/**
	 * Once the PDF is rendered, we can find the height of the content very easily by having a hidden
	 * element (the class is ".bottom-marker") as the very last piece of content (this is added by the
	 * viewer component, so it doesn't appear in the document itself). The top position of the first element
	 * (to account for margins) subtracted from the top position of the hidden element is the content height.
	 * Only the footer makes use of this information right now.
	 *
	 * @param contentDefinitions Post-rendering list of content in the PDF, which includes positioning info
	 */
	finishedRendering(): void {
		window.setTimeout(() => {
			this.busy = false;
			this.changeDetectorRef.detectChanges();
		}, 100);
	}

	resetLetterhead(): void {
		this.busy = true;
		let newContent = '';
		const defaultFooter = this.data.officeId
			? this.businessFooter || this.defaultFooter
			: this.defaultFooter;

		switch (this.data.mode) {
			case 'footer':
				if (this.footer.first.matches) {
					newContent = defaultFooter.global.content;
					this.footer = cloneDeep(defaultFooter);
				} else {
					switch (this.currentFooter) {
						case FooterName.FIRST:
							newContent = defaultFooter.first.matches
								? defaultFooter.global.content
								: defaultFooter.first.content;
							this.footer = {
								...this.footer,
								first: {
									...cloneDeep(defaultFooter.first),
									matches: false, // prevents a weird UX experience
								},
							};
							break;
						case FooterName.GLOBAL:
							newContent = defaultFooter.global.content;
							this.footer = {
								...this.footer,
								global: cloneDeep(defaultFooter.global),
							};
							break;
					}
				}
				break;
			case 'letterhead':
				newContent = this.data.officeId
					? this.businessLetterhead || this.defaultLetterhead
					: this.defaultLetterhead;
				break;
		}

		if (this.content === newContent) {
			this.busy = false; // nothing to re-render, so we're done
		} else {
			this.content = newContent;
		}
	}

	saveLetterhead(): void {
		if (!this.busy) {
			this.busy = true;
			this.save = true;
			this.footer[this.currentFooter].content = this.content;
			const repo = this.data.officeId
				? this.businessAddressesService
				: this.businessesServiceRepo;

			repo
				.Update(this.data.officeId ? this.data.officeId : this.businessId, {
					[this.data.mode]:
						this.data.mode === 'letterhead' ? this.content : this.footer,
				})
				.finally(() => {
					this.dialogRef.close();
					this.busy = false;
					this.save = false; // in case of a problem, don't disable the button
				});
		}
	}

	setGlobalFooterSwitch(): void {
		this.globalFooter = this.footer.first.enabled || this.footer.global.enabled;
	}

	switchFooter(newFooter: FooterName): void {
		this.busy = true;
		this.currentFooter = newFooter;
		if (this.footer.first.matches) {
			this.footer.first.enabled = true;
			this.footer.global.enabled = true;
			this.setGlobalFooterSwitch();
		}
		switch (newFooter) {
			case FooterName.FIRST:
				this.footer.global.content = this.content;
				this.content = this.footer.first.matches
					? this.footer.global.content
					: this.footer.first.content;
				if (this.footer.global.content === this.content) {
					this.busy = false;
				}
				break;
			case FooterName.GLOBAL:
				this.footer.first.content = this.content;
				this.footer.first.height = this.footer.global.height;
				this.content = this.footer.global.content;
				if (this.footer.first.content === this.content) {
					this.busy = false;
				}
				break;
		}
	}

	toggleFootersEnablement(): void {
		this.footer.first.enabled = !this.footer.first.enabled;
		this.footer.global.enabled = !this.footer.global.enabled;
		this.setGlobalFooterSwitch();
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	updateLetterhead(change: EventObj<any>): void {
		this.content = change.event.level.content;
	}

	waitForRender(content: string): void {
		if (content !== this.content) {
			this.busy = true;
			this.content = content || '<p></p>';
		}
	}
}
